/**
 * Bexio contact type migration: converts Bexio contacts that were wrongly created
 * as companies (Firma) into persons, using first/last name from the customer profile.
 * Mirrors `BexioContactTypeMigrationResult` in the admin backend.
 */

export type BexioContactMigrationStatus =
  | 'wouldConvert'
  | 'converted'
  | 'skipped'
  | 'duplicate'
  | 'failed';

/** Bexio contact type ids. */
export const BEXIO_CONTACT_TYPE = { COMPANY: 1, PERSON: 2 } as const;

/** Another Bexio contact with the same email as a company contact. */
export interface BexioDuplicateContact {
  bexioId: number;
  /** 1 = company, 2 = person. */
  contactTypeId: number;
  name1?: string | null;
  name2?: string | null;
  email?: string | null;
}

export interface BexioContactMigrationEntry {
  userId: string;
  email?: string | null;
  /** Tenant of the customer; Bexio contact ids are only unique within the tenant's Bexio account. */
  tenantId: string;
  bexioId: number;
  /** Current company name in Bexio (name_1). */
  currentName1?: string | null;
  /** Current name addition in Bexio (name_2). */
  currentName2?: string | null;
  /** Last name the contact will get. */
  newName1?: string | null;
  /** First name the contact will get. */
  newName2?: string | null;
  status: BexioContactMigrationStatus;
  reason?: string | null;
  /** Set for status `duplicate`: the other contacts using the same email. */
  duplicateContacts?: BexioDuplicateContact[];
}

export interface BexioContactMigrationResult {
  dryRun: boolean;
  checked: number;
  toConvert: number;
  converted: number;
  skipped: number;
  failed: number;
  /** Company contacts left unchanged because another contact uses the same email. */
  duplicates?: number;
  entries: BexioContactMigrationEntry[];
}

export interface BexioContactRef {
  tenantId: string;
  bexioId: number;
}

export interface BexioContactMigrationParams {
  dryRun: boolean;
  /** Contacts that must stay companies, identified by tenant and Bexio contact id. */
  excludedContacts?: BexioContactRef[];
}
